import React, { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { useParams, Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Globe } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';
import { RichTextContent } from '@/components/RichTextEditor';
import { Carousel } from '@/components/Carousel';
import { useCartStore } from '@/store/cartStore';

type Producer = Database['public']['Tables']['producers']['Row'];
type Product = Database['public']['Tables']['products']['Row'];
type Category = Database['public']['Tables']['categories']['Row'];

// Map of category IDs to their names
const FALLBACK_CATEGORIES: Record<string, string> = {
  'kryddor': 'Kryddor',
  'rubs': 'Rubs & Kryddblandningar',
  'tillbehör': 'Tillbehör',
  'chark': 'Charkuterier',
  'sås': 'Såser & Dressingar',
  'dryck': 'Dryck',
};

export function ProducerDetail() {
  const { id } = useParams();
  const [producer, setProducer] = useState<Producer | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [stockLevels, setStockLevels] = useState<Record<string, number>>({});
  const [profileData, setProfileData] = useState<{
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string | null;
  } | null>(null);
  
  // Get category name by ID from fetched categories or fallback map
  const getCategoryName = (categoryId: string): string => {
    // First try to find the category in our fetched categories
    const category = categories.find(c => c.id === categoryId);
    if (category?.name) return category.name;
    
    // Then try the fallback map
    if (FALLBACK_CATEGORIES[categoryId]) return FALLBACK_CATEGORIES[categoryId];
    
    // Finally, use a readable version of the ID if it's not a UUID
    if (!categoryId.includes('-')) return categoryId;
    
    // Last resort, show a generic name
    return 'Produkter';
  };

  // Group products by category
  const productsByCategory = React.useMemo(() => {
    const grouped: Record<string, { id: string, name: string, products: Product[] }> = {};
    
    products.forEach(product => {
      const categoryId = product.category_id;
      if (!categoryId) return;
      
      if (!grouped[categoryId]) {
        grouped[categoryId] = {
          id: categoryId,
          name: getCategoryName(categoryId),
          products: []
        };
      }
      
      grouped[categoryId].products.push(product);
    });
    
    return Object.values(grouped);
  }, [products, categories]);

  // Get visible products (all or filtered by category)
  const visibleProducts = React.useMemo(() => {
    if (!selectedCategory) return products;
    return products.filter(p => p.category_id === selectedCategory);
  }, [products, selectedCategory]);

  useEffect(() => {
    async function fetchProducerAndProducts() {
      if (!id) return;

      try {
        const [producerResult, productsResult, categoriesResult, profileResult] = await Promise.all([
          supabase
            .from('producers')
            .select('*')
            .eq('id', id)
            .eq('is_approved', true)
            .single(),
          supabase
            .from('products')
            .select('*, stock_level')
            .eq('producer_id', id)
            .eq('is_published', true),
          supabase
            .from('categories')
            .select('*'),
          supabase
            .from('profiles')
            .select('first_name, last_name, phone, email')
            .eq('id', id)
            .single()
        ]);

        if (producerResult.error) {
          logger.error('Error fetching producer:', producerResult.error);
          return;
        }

        if (productsResult.error) {
          logger.error('Error fetching products:', productsResult.error);
          return;
        }

        if (categoriesResult.error) {
          logger.error('Error fetching categories:', categoriesResult.error);
        } else {
          setCategories(categoriesResult.data || []);
        }

        if (!profileResult.error && profileResult.data) {
          setProfileData({
            firstName: profileResult.data.first_name,
            lastName: profileResult.data.last_name,
            phone: profileResult.data.phone,
            email: profileResult.data.email
          });
        }

        setProducer(producerResult.data);
        setProducts(productsResult.data || []);
        
        // Create a stock levels map for quick access
        if (productsResult.data) {
          const levels = productsResult.data.reduce((acc, product) => ({
            ...acc,
            [product.id]: product.stock_level || 0
          }), {});
          setStockLevels(levels);
        }
      } catch (error) {
        logger.error('Error in data fetching:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchProducerAndProducts();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-8" />
          <div className="space-y-8">
            <div className="h-64 bg-gray-200 rounded-lg" />
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!producer) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center text-gray-500">
          Producenten hittades inte
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="lg:grid lg:grid-cols-2 lg:gap-x-8 mb-12">
        <div>
          {producer.logo_url ? (
            <img
              src={producer.logo_url}
              alt={producer.company_name}
              className="w-full h-64 object-contain bg-gray-50 rounded-lg"
            />
          ) : (
            <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
              <span className="text-gray-400 text-4xl font-medium">
                {producer.company_name.charAt(0)}
              </span>
            </div>
          )}
          
          <h1 className="mt-8 text-3xl font-extrabold text-gray-900">
            {producer.company_name}
          </h1>
          
          <div className="mt-4 text-gray-500">
            <RichTextContent content={producer.description || ''} />
          </div>

          <div className="mt-6 flex space-x-6">
            {producer.website_url && 
             producer.website_url.trim() !== '' && 
             !/^(https?:\/\/)?$/.test(producer.website_url.trim()) && (
              <a
                href={producer.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-500"
              >
                <Globe className="h-6 w-6" />
              </a>
            )}
            {producer.facebook_url && 
             producer.facebook_url.trim() !== '' && 
             !/^(https?:\/\/)?$/.test(producer.facebook_url.trim()) && (
              <a
                href={producer.facebook_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-500"
              >
                <Facebook className="h-6 w-6" />
              </a>
            )}
            {producer.instagram_url && 
             producer.instagram_url.trim() !== '' && 
             !/^(https?:\/\/)?$/.test(producer.instagram_url.trim()) && (
              <a
                href={producer.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-500"
              >
                <Instagram className="h-6 w-6" />
              </a>
            )}
            {producer.twitter_url && 
             producer.twitter_url.trim() !== '' && 
             !/^(https?:\/\/)?$/.test(producer.twitter_url.trim()) && (
              <a
                href={producer.twitter_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-500"
              >
                <Twitter className="h-6 w-6" />
              </a>
            )}
          </div>
        </div>

        <div className="mt-10 lg:mt-0">
          {/* Contact information section */}
          {(producer.show_name || producer.show_phone || producer.show_email) && profileData && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Kontaktinformation</h3>
              <dl className="space-y-3">
                {producer.show_name && profileData.firstName && profileData.lastName && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Namn</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {profileData.firstName} {profileData.lastName}
                    </dd>
                  </div>
                )}
                
                {producer.show_phone && profileData.phone && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Telefon</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <a href={`tel:${profileData.phone}`} className="text-green-600 hover:text-green-700">
                        {profileData.phone}
                      </a>
                    </dd>
                  </div>
                )}
                
                {producer.show_email && profileData.email && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">E-post</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <a href={`mailto:${profileData.email}`} className="text-green-600 hover:text-green-700">
                        {profileData.email}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      </div>

      {/* Products section */}
      <div className="mt-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Produkter från {producer.company_name}
        </h2>
        
        {products.length === 0 ? (
          <p className="text-gray-500">Inga produkter tillgängliga ännu.</p>
        ) : (
          <>
            {/* Category selector */}
            {productsByCategory.length > 1 && (
              <div className="mb-8">
                <div className="flex overflow-x-auto pb-2 space-x-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-4 py-2 rounded-full whitespace-nowrap ${
                      selectedCategory === null 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    Alla produkter ({products.length})
                  </button>
                  
                  {productsByCategory.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-4 py-2 rounded-full whitespace-nowrap ${
                        selectedCategory === category.id 
                          ? 'bg-green-600 text-white' 
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      {category.name} ({category.products.length})
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Products display */}
            {selectedCategory === null ? (
              // Show products by category with carousels
              <div className="space-y-16">
                {productsByCategory.map(category => (
                  <div key={category.id}>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {category.name}
                      </h3>
                      
                      {category.products.length > 3 && (
                        <button
                          onClick={() => setSelectedCategory(category.id)}
                          className="text-green-600 hover:text-green-700 font-medium"
                        >
                          Visa alla
                        </button>
                      )}
                    </div>
                    
                    <Carousel
                      itemsToShow={window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3}
                      showArrows={category.products.length > 3}
                      showDots={window.innerWidth < 640}
                    >
                      {category.products.map(product => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </Carousel>
                  </div>
                ))}
              </div>
            ) : (
              // Show grid of products for selected category
              <div className="grid grid-cols-1 gap-y-8 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-3 xl:grid-cols-4">
                {visibleProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Product card component for consistent display
const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  const addItem = useCartStore((state) => state.addItem);
  const stockLevel = product.stock_level || 0;
  const isInStock = stockLevel > 0;
  
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation to product page
    e.stopPropagation(); // Prevent event bubbling
    
    if (isInStock) {
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        producerId: product.producer_id
      });
    }
  };

  return (
    <div className="group relative">
      <Link
        to={`/products/${product.slug}`}
        className="block"
      >
        <div className="aspect-h-3 aspect-w-4 overflow-hidden rounded-lg bg-gray-100">
          <img
            src={product.image_urls?.[0] || '/placeholder-product.jpg'}
            alt={product.name}
            className="h-full w-full object-cover object-center group-hover:opacity-75"
          />
        </div>
        <h3 className="mt-4 text-sm text-gray-700">{product.name}</h3>
        <p className="mt-1 text-lg font-medium text-gray-900">
          {product.price.toLocaleString('sv-SE')} Kr
        </p>
        
        {/* Stock level indicator */}
        <p className={`mt-1 text-sm ${isInStock ? 'text-green-600' : 'text-red-600'}`}>
          {isInStock 
            ? `I lager: ${stockLevel}` 
            : 'Slut i lager'}
        </p>
      </Link>
      
      {/* Add to cart button */}
      <button
        onClick={handleAddToCart}
        disabled={!isInStock}
        className={`mt-2 w-full py-2 px-4 rounded-md text-sm font-medium text-white ${
          isInStock
            ? 'bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500'
            : 'bg-gray-300 cursor-not-allowed'
        }`}
        data-testid="add-to-cart"
      >
        Lägg i kundvagn
      </button>
    </div>
  );
};